<template>
  <div
    v-if="product && attributes"
    class="configurable"
    :class="{ 'is-outlet-product': isOutletProduct }"
  >
    <ProductConfigurableAttribute
      v-for="attribute in attributes"
      :key="`${product.id}_${attribute.attribute_code}`"
      :attribute="attribute"
      :product="product"
      :selected-options="selectedOptions"
      :parent-type="parentType"
      @selected="selected"
    />
  </div>

  <slot
    name="oos"
    v-bind="{ oos, selectedOptions }"
  />
</template>

<script lang="ts" setup>
import type {
  MagentoConfigurableProductOptionsValue,
  MagentoConfigurableProductCartItemInput,
  MagentoConfigurableProduct,
  MagentoConfigurableVariant,
} from '~layers/app/types/magento'

export interface Props {
  product: MagentoConfigurableProduct
  parentType?: string
  isOutletProduct?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'selected': [item: Record<string, MagentoConfigurableProductCartItemInput | null>]
  'selected-options': [options: Record<string, MagentoConfigurableProductOptionsValue>]
}>()

const selectedOptions = ref<Record<string, MagentoConfigurableProductOptionsValue>>({})

const selected = (attributeOption: Record<string, MagentoConfigurableProductOptionsValue | null>) => {
  selectedOptions.value = {
    ...selectedOptions.value,
    ...attributeOption,
  }

  emit('selected-options', selectedOptions.value)

  if (!Object.values(selectedOptions.value).some(item => item)) {
    emit('selected', { [props.product.sku]: null })
  }
  else {
    emit('selected', {
      [props.product.sku]: outOfStock.value
        ? null
        : {
            parent_sku: props.product.sku,
            data: {
              quantity: 1,
              sku: selectedVariant.value.product.sku,
            },
          },
    })
  }
}

const selectedVariant = computed<MagentoConfigurableVariant | null>(() =>
  props.product.variants.find((variant: MagentoConfigurableVariant) => {
    let variantAttribute
    for (const attributeCode in selectedOptions.value) {
      variantAttribute = variant.attributes.find((varAttr: any) => varAttr.code === attributeCode)
      if (variantAttribute?.value_index !== selectedOptions.value[attributeCode]?.value_index) {
        return false
      }
    }
    return !!Object.keys(selectedOptions.value).length
  }) || null,
)

const outOfStock = computed(() =>
  selectedVariant.value?.product?.stock_status === 'OUT_OF_STOCK' || props.product.stock_status === 'OUT_OF_STOCK',
)

const oos = computed(() =>
  outOfStock.value ? [selectedVariant.value?.product || props.product] : [],
)

const attributes = computed(() => {
  return props.product?.configurable_options
})

const autoSelected: Record<string, MagentoConfigurableProductOptionsValue> = {}

for (const attribute of attributes.value ?? []) {
  if (attribute.values?.length === 1) {
    autoSelected[attribute.attribute_code] = attribute.values[0]
  }
}

if (Object.keys(autoSelected).length === 1) {
  selected(autoSelected)
}
</script>
